/**
 * Error Page Configuration Constants
 *
 * Centralized configuration for error page display based on HTTP status codes.
 */

import {
  AlertTriangle,
  LogIn,
  OctagonX,
  Search,
  ServerCrash,
  ShieldAlert,
  Wifi,
  WifiOff,
  Clock,
  type LucideIcon,
} from 'lucide-react';

/**
 * Error page configuration by status code
 */
export interface ErrorConfig {
  icon: LucideIcon;
  title: string;
  message: string;
  showRetry: boolean;
  showHome: boolean;
  showSignIn: boolean;
  showBack: boolean;
  showStack: boolean;
}

export const errorConfigs: Record<number | string, ErrorConfig> = {
  // Special error types (non-HTTP)
  clerk: {
    icon: ShieldAlert,
    title: 'Authentication Service Error',
    message: 'Unable to connect to the authentication service. Please check your internet connection and try again.',
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: false,
  },
  // HTTP status codes
  400: {
    icon: OctagonX,
    title: 'Bad Request',
    message: 'The request was invalid. Please check your input and try again.',
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: true,
  },
  401: {
    icon: LogIn,
    title: 'Authentication Required',
    message: 'You need to be signed in to access this page.',
    showRetry: false,
    showHome: false,
    showSignIn: true,
    showBack: true,
    showStack: false,
  },
  403: {
    icon: AlertTriangle,
    title: 'Access Denied',
    message: "You don't have permission to access this resource.",
    showRetry: false,
    showHome: true,
    showSignIn: false,
    showBack: true,
    showStack: false,
  },
  404: {
    icon: Search,
    title: 'Not Found',
    message: "The page you're looking for doesn't exist or has been moved.",
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: false,
  },
  409: {
    icon: AlertTriangle,
    title: 'Conflict',
    message: 'There was a conflict with the current state. Please refresh and try again.',
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: false,
  },
  429: {
    icon: Clock,
    title: 'Too Many Requests',
    message: 'You have made too many requests. Please wait a moment and try again.',
    showRetry: true,
    showHome: false,
    showSignIn: false,
    showBack: true,
    showStack: false,
  },
  500: {
    icon: ServerCrash,
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: true,
  },
  502: {
    icon: Wifi,
    title: 'Bad Gateway',
    message: 'We are having trouble connecting to our servers. Please try again.',
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: true,
  },
  503: {
    icon: WifiOff,
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable. Please try again later.',
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: false,
  },
  504: {
    icon: Clock,
    title: 'Request Timeout',
    message: 'The request took too long to complete. Please try again.',
    showRetry: true,
    showHome: true,
    showSignIn: false,
    showBack: false,
    showStack: false,
  },
};

export const defaultErrorConfig: ErrorConfig = {
  icon: OctagonX,
  title: 'Something Went Wrong',
  message: 'An unexpected error occurred. Please try again.',
  showRetry: true,
  showHome: true,
  showSignIn: false,
  showBack: false,
  showStack: true,
};
